// ─────────────────────────────────────────────────────────────────────────────
// src/features/mdx/components/AIQueryBuilder.tsx
//
// VERSION AI v2 :
//   • Pré-sélection automatique : dimensions Cod Dep / Famille / Sousfamille
//     en lignes + mesure "Vente PTTC" sélectionnée + période mois courant
//   • Bouton ✨ "Remplir par IA" : configure ET exécute directement (pas de
//     bouton Exécuter séparé) — appelle Claude API + lance runQuery auto
//   • Solde et Démarque supprimés du bloc Période & Filtres
//   • Matrice croisée (PivotMatrix) identique à QueryBuilder
//   • Toutes les autres fonctionnalités conservées intégralement
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hooks";
import THEME from "../../../shared/ui/theme";
import {
  CUBE_NAME,
  fetchDimensions,
  fetchMeasures,
} from "../../cube/cubeSlice";
import { DimensionSelection, DragPayload } from "../../cube/types";
import {
  addToColumns,
  addToRows,
  moveToColumns,
  moveToRows,
  removeFromColumns,
  removeFromRows,
  reorderColumns,
  reorderRows,
  resetQuery,
  setDateRange,
  setTopN,
  toggleMeasure,
} from "../../cube/querySlice";
import { getMembers } from "../../cube/api";
import { getPrevisionFamilles, postPrevisionF } from "../api";
import { useForecastDepots } from "../useForecastDepots";
import { SavedQuery } from "../../saved-queries/api";
import { runQuery } from "../../cube/resultsSlice";
import EvolutionView from "../../cube/components/EvolutionView";
import ComparisonView from "../../cube/components/ComparisonView";
import Dashboard from "../../cube/components/Dashboard";
import SaveQueryModal from "../../saved-queries/components/SaveQueryModal";
import MemberFilterPopover from "../../cube/components/MemberFilterPopover";
import SavedQueriesDropdown from "../../saved-queries/components/SavedQueriesDropdown";
import { applyDateMask, dayBefore, displayToIso, fixYear, isValidDate, isoToDisplay } from "../../../shared/lib/date";
import { CollapseToggleBtn } from "../../cube/components/CollapseToggleBtn";
import { DimensionPanel } from "../../cube/components/DimensionPanel";
import { buildColGroups } from "../../cube/pivotColumns";
import { colors } from "../../../shared/ui/tokens";

// ── AVDASH brand palette ──────────────────────────────────────────────────────
const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  roseDark: colors.accent[500],
  roseMed: colors.accent[100],
  roseLight: colors.accent[50],
  roseSoft: colors.neutral[100],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  amber: colors.warning[500],
  amberLight: colors.warning[100],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
};

type ViewMode = "table" | "dashboard" | "comparison" | "evolution";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

type FlatCell = { value: string; rowSpan: number } | null;
interface FlatRow {
  dimCells: (FlatCell | null)[];
  dataValues: any[];
}

function flattenRows(
  rows: any[][],
  rowCols: number,
  keepOrigIdx: number[],
): FlatRow[] {
  if (rows.length === 0) return [];
  const pickData = (row: any[]) => keepOrigIdx.map((i) => row[rowCols + i]);
  if (rowCols === 0)
    return rows.map((row) => ({ dimCells: [], dataValues: pickData(row) }));
  if (rowCols === 1)
    return rows.map((row) => ({
      dimCells: [{ value: String(row[0] ?? ""), rowSpan: 1 }],
      dataValues: pickData(row),
    }));

  const result: FlatRow[] = rows.map((row) => ({
    dimCells: Array(rowCols).fill(null) as (FlatCell | null)[],
    dataValues: pickData(row),
  }));
  for (let level = 0; level < rowCols; level++) {
    let i = 0;
    while (i < rows.length) {
      const key = String(rows[i][level] ?? "");
      let j = i + 1;
      while (j < rows.length) {
        let sameParent = true;
        for (let p = 0; p < level; p++) {
          if (String(rows[j][p] ?? "") !== String(rows[i][p] ?? "")) {
            sameParent = false;
            break;
          }
        }
        if (!sameParent || String(rows[j][level] ?? "") !== key) break;
        j++;
      }
      result[i].dimCells[level] = { value: key, rowSpan: j - i };
      i = j;
    }
  }
  return result;
}

function fmtNum(val: any): string {
  if (val == null || val === "") return "";
  const n = typeof val === "number" ? val : parseFloat(val);
  if (isNaN(n)) return String(val);
  return Math.abs(n).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
}

// ─────────────────────────────────────────────────────────────────────────────
// PivotMatrix
// ─────────────────────────────────────────────────────────────────────────────
interface PivotMatrixProps {
  dragPayload?: React.MutableRefObject<DragPayload | null>;
  onDrop?: (zone: "rows" | "columns", targetIdx: number | null) => void;
  active?: boolean;
  /** Incrémenté à chaque "Exécuter" → relance l'animation fadeSlideIn */
  animKey?: number;
}

const PivotMatrix: React.FC<PivotMatrixProps> = ({
  dragPayload,
  onDrop,
  active = true,
  animKey = 0,
}) => {
  const results = useAppSelector((s) => (s as any).results);
  const rowDims = useAppSelector((s) => s.query.rows);
  const colDims = useAppSelector((s) => s.query.columns);
  const topN = useAppSelector((s) => s.query.topN);

  const [colOver, setColOver] = useState(false);
  const [rowOver, setRowOver] = useState(false);
  const [rowFilter, setRowFilter] = useState("");

  // ── Filtre colonnes ─────────────────────────────────────────────────────
  const [colFilterOpen, setColFilterOpen] = useState(false);
  const [colFilterQuery, setColFilterQuery] = useState("");
  const [colMembersFromApi, setColMembersFromApi] = useState<string[]>([]);
  const [colMembersSelected, setColMembersSelected] =
    useState<Set<string> | null>(null);
  const [colMembersLoading, setColMembersLoading] = useState(false);

  const colFilterRef = useRef<HTMLDivElement>(null);
  const colFilterInputRef = useRef<HTMLInputElement>(null);

  // FIX ① : reset du cache membres quand les dimensions colonnes changent
  useEffect(() => {
    setColMembersFromApi([]);
    setColMembersSelected(null);
    setColFilterQuery("");
  }, [
    JSON.stringify(
      colDims.map(
        (d) => `${d.dimensionName}|${d.hierarchyName}|${d.levelName}`,
      ),
    ),
  ]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        colFilterRef.current &&
        !colFilterRef.current.contains(e.target as Node)
      )
        setColFilterOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // FIX ① : toujours re-fetcher (pas seulement si cache vide)
  const handleColFilterFocus = async () => {
    setColFilterOpen(true);
    if (colDims.length === 0) return;

    // On re-fetch à chaque ouverture pour avoir des données fraîches
    setColMembersLoading(true);
    try {
      // Récupère les membres de TOUTES les dimensions colonnes (concat)
      const allMembers: string[] = [];
      for (const dim of colDims) {
        const members = await getMembers(
          CUBE_NAME,
          dim.dimensionName,
          dim.hierarchyName ?? "",
          dim.levelName ?? "",
        )
          .then(({ data }) => data ?? [])
          .catch(() => [] as string[]);
        members.forEach((m) => {
          if (!allMembers.includes(m)) allMembers.push(m);
        });
      }
      if (allMembers.length > 0) {
        setColMembersFromApi(allMembers);
        // Préserve la sélection courante si déjà filtrée
        if (colMembersSelected !== null) {
          const newSel = new Set(
            [...colMembersSelected].filter((m) => allMembers.includes(m)),
          );
          setColMembersSelected(
            newSel.size === allMembers.length ? null : newSel,
          );
        }
      }
    } catch {
      /* silencieux — on garde les données précédentes ou le fallback table */
    } finally {
      setColMembersLoading(false);
    }
  };

  const toggleColMember = (member: string) => {
    const all =
      colMembersFromApi.length > 0 ? colMembersFromApi : visibleColGroupLabels;
    const base =
      colMembersSelected === null ? new Set(all) : new Set(colMembersSelected);
    if (base.has(member)) base.delete(member);
    else base.add(member);
    setColMembersSelected(base);
  };

  const { loading, error } = results || {};
  const rawData = (results || {}).data;
  const apiColumns: string[] = rawData?.columns ?? [];
  const apiRows: any[][] = rawData?.rows ?? [];
  const hasData = apiColumns.length > 0 && apiRows.length > 0;

  const rowColCount = hasData ? Math.min(rowDims.length, apiColumns.length) : 0;
  const rowHeaders = hasData ? apiColumns.slice(0, rowColCount) : [];
  const allDataCols = hasData ? apiColumns.slice(rowColCount) : [];
  const colGroups = buildColGroups(allDataCols);

  const useGroupedHeader =
    hasData &&
    (colGroups.length > 1 ||
      colGroups.some((g) => g.children.length > 1) ||
      colDims.length >= 1);

  // ✅ Supprimer la ligne "All" (membre All OLAP = total global, déjà dans le footer)
  let displayRows = apiRows.filter((row) => {
    for (let i = 0; i < rowColCount; i++) {
      const v = String(row[i] ?? "")
        .trim()
        .toLowerCase();
      if (v === "all" || v === "unknown") return false;
    }
    return true;
  });

  if (hasData && topN !== null && topN > 0 && allDataCols.length > 0) {
    displayRows = [...displayRows]
      .sort(
        (a, b) =>
          (parseFloat(b[rowColCount]) || 0) - (parseFloat(a[rowColCount]) || 0),
      )
      .slice(0, topN);
  }

  const keepOrigIdx = colGroups.flatMap((g) =>
    g.children.map((c) => c.origIdx),
  );
  const flatRows = hasData
    ? flattenRows(displayRows, rowColCount, keepOrigIdx)
    : [];
  const visibleColGroupLabels = colGroups.map((g) => g.label);

  // ✅ Les sous-colonnes réelles issues des résultats (DC, DG, DP, PR, SF…)
  const allChildrenLabels = colGroups.flatMap((g) =>
    g.children.map((c) => c.label),
  );
  // L'API reste appelée mais on affiche les children, pas les membres dimension
  const allAvailableMembers = (
    allChildrenLabels.length > 0 ? allChildrenLabels : visibleColGroupLabels
  ).filter((m) => m.trim().toLowerCase() !== "all");
  const searchedMembers = allAvailableMembers.filter(
    (m) =>
      colFilterQuery.trim() === "" ||
      m.toLowerCase().includes(colFilterQuery.trim().toLowerCase()),
  );

  // ✅ On filtre les enfants à l'intérieur de chaque groupe (Tva reste,
  //    mais on n'affiche que les children cochés : DC, DG, PR…)
  const isUnknown = (s: string) => s.trim().toLowerCase() === "unknown";

  const filteredColGroups = (
    colMembersSelected === null
      ? colGroups
      : colGroups
          .map((g) => ({
            ...g,
            children: g.children.filter((c) => colMembersSelected.has(c.label)),
          }))
          .filter((g) => g.children.length > 0)
  )
    .filter((g) => !isUnknown(g.label))
    .map((g) => ({
      ...g,
      children: g.children.filter((c) => !isUnknown(c.label)),
    }))
    .filter((g) => g.children.length > 0);

  const filteredFlatRows = !hasData
    ? []
    : rowFilter.trim() === ""
      ? flatRows
      : flatRows.filter(
          (fr) =>
            fr.dimCells.some(
              (cell) =>
                cell !== null &&
                cell.value.toLowerCase().includes(rowFilter.toLowerCase()),
            ) || fr.dimCells.every((cell) => cell === null),
        );

  const filteredKeepOrigIdx = filteredColGroups.flatMap((g) =>
    g.children.map((c) => c.origIdx),
  );

  const grandTotals = hasData
    ? filteredKeepOrigIdx.map((origIdx) => {
        const vals = displayRows.map((r) => {
          const v = r[rowColCount + origIdx];
          return typeof v === "number" ? v : parseFloat(v) || 0;
        });
        return Math.abs(vals.reduce((a, b) => a + b, 0));
      })
    : [];

  const allChildren = filteredColGroups.flatMap((g) => g.children);
  const DIM_COL_W = 140;

  const thDim: React.CSSProperties = {
    background: NW.violetDark,
    color: "#FFFFFF",
    padding: "10px 14px",
    textAlign: "left",
    fontWeight: 700,
    fontSize: 12,
    fontFamily: THEME.fonts.heading,
    border: `1px solid rgba(255,255,255,0.12)`,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  };
  const thGroup: React.CSSProperties = {
    background: NW.rose,
    color: "#FFFFFF",
    padding: "8px 14px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 12,
    fontFamily: THEME.fonts.heading,
    border: `1px solid ${NW.roseDark}`,
    whiteSpace: "nowrap",
    letterSpacing: 0.4,
  };
  const thChild: React.CSSProperties = {
    background: NW.roseLight,
    color: NW.roseDark,
    padding: "7px 12px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 11,
    fontFamily: THEME.fonts.body,
    border: `1px solid ${NW.roseMed}`,
    whiteSpace: "nowrap",
  };

  const renderContent = () => {
    if (loading)
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 320,
            gap: 12,
          }}
        >
          <span className="spinner" />
          <span
            style={{
              color: NW.gray500,
              fontSize: 13,
              fontFamily: THEME.fonts.body,
            }}
          >
            Chargement des données…
          </span>
        </div>
      );

    if (error)
      return (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            color: NW.danger,
            background: NW.dangerLight,
            borderRadius: 8,
            fontSize: 13,
            fontFamily: THEME.fonts.body,
          }}
        >
          ⚠ {error}
        </div>
      );

    if (hasData && active)
      return (
        // FIX ③ : key=animKey force le re-mount → relance l'animation CSS
        <div
          key={animKey}
          style={{
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: 520,
            animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <table
            style={{
              borderCollapse: "collapse",
              fontSize: 12,
              fontFamily: THEME.fonts.body,
              minWidth: 480,
              tableLayout: "auto",
            }}
          >
            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                {rowHeaders.map((h, i) => (
                  <th
                    key={i}
                    rowSpan={useGroupedHeader ? 2 : 1}
                    style={{
                      ...thDim,
                      minWidth: DIM_COL_W,
                      maxWidth: 200,
                      position: "sticky",
                      left: i * DIM_COL_W,
                      zIndex: 20,
                      boxShadow: "2px 0 6px rgba(15,23,42,0.08)",
                      borderRight:
                        i === rowHeaders.length - 1
                          ? `3px solid ${NW.violet}`
                          : `1px solid rgba(255,255,255,0.15)`,
                      paddingBottom: i === 0 ? 4 : undefined,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <span>{h}</span>
                      {i === 0 && (
                        <input
                          type="text"
                          value={rowFilter}
                          onChange={(e) => setRowFilter(e.target.value)}
                          placeholder="🔍 Filtrer…"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: "rgba(255,255,255,0.15)",
                            border: `1px solid ${rowFilter ? NW.white : "rgba(255,255,255,0.3)"}`,
                            borderRadius: 6,
                            padding: "3px 8px",
                            fontSize: 11,
                            color: "#FFFFFF",
                            outline: "none",
                            width: "100%",
                            fontFamily: THEME.fonts.body,
                            boxSizing: "border-box",
                          }}
                        />
                      )}
                    </div>
                  </th>
                ))}
                {useGroupedHeader
                  ? filteredColGroups.map((g, gi) => (
                      <th
                        key={gi}
                        colSpan={g.children.length}
                        style={{
                          ...thGroup,
                          borderBottom: `2px solid ${NW.roseDark}`,
                        }}
                      >
                        {g.label}
                      </th>
                    ))
                  : allChildren.map((c, ci) => (
                      <th key={ci} style={thChild}>
                        {c.label}
                      </th>
                    ))}
              </tr>
              {useGroupedHeader && (
                <tr>
                  {filteredColGroups.flatMap((g, gi) =>
                    g.children.map((c, ci) => (
                      <th
                        key={`${gi}-${ci}`}
                        style={{
                          ...thChild,
                          borderTop: `1px solid ${NW.roseMed}`,
                        }}
                      >
                        {c.label}
                      </th>
                    )),
                  )}
                </tr>
              )}
            </thead>
            <tbody>
              {filteredFlatRows.map((fr, ri) => {
                const isEven = ri % 2 === 0;
                const rowBg = isEven ? NW.white : NW.gray50;
                const dataValues = filteredKeepOrigIdx.map(
                  (origIdx) => fr.dataValues[keepOrigIdx.indexOf(origIdx)],
                );
                return (
                  <tr
                    key={ri}
                    style={{ background: rowBg }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLElement).style.background =
                        NW.violetSoft)
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLElement).style.background =
                        rowBg)
                    }
                  >
                    {fr.dimCells.map((cell, ci) =>
                      cell === null ? null : (
                        <td
                          key={ci}
                          rowSpan={cell.rowSpan}
                          style={{
                            padding: "8px 14px",
                            border: `1px solid ${NW.violetMed}`,
                            borderRight:
                              ci === rowColCount - 1
                                ? `3px solid ${NW.violet}`
                                : `1px solid ${NW.violetMed}`,
                            fontWeight: ci === 0 ? 700 : 500,
                            color: NW.violetDark,
                            background: isEven ? NW.violetSoft : "#EEF4FB",
                            verticalAlign: "middle",
                            position: "sticky",
                            left: ci * DIM_COL_W,
                            zIndex: 2,
                            whiteSpace: "nowrap",
                            minWidth: DIM_COL_W,
                            paddingLeft: ci > 0 ? 22 : 14,
                            boxShadow:
                              ci === rowColCount - 1
                                ? "2px 0 6px rgba(24,75,140,0.07)"
                                : undefined,
                          }}
                        >
                          {cell.value || (ci > 0 ? "—" : "")}
                        </td>
                      ),
                    )}
                    {dataValues.map((val, di) => {
                      const raw =
                        val != null && val !== "" ? parseFloat(val) : null;
                      const empty = raw === null || isNaN(raw as number);
                      const num = empty ? null : Math.abs(raw as number);
                      return (
                        <td
                          key={di}
                          style={{
                            padding: "8px 14px",
                            border: `1px solid ${NW.gray200}`,
                            textAlign: "right",
                            color: empty ? NW.gray500 : NW.gray800,
                            background: rowBg,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {num !== null ? (
                            num.toLocaleString("fr-FR", {
                              maximumFractionDigits: 2,
                            })
                          ) : (
                            <span style={{ color: NW.gray300 }}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            {displayRows.length > 1 && (
              <tfoot style={{ position: "sticky", bottom: 0, zIndex: 5 }}>
                <tr>
                  {rowHeaders.map((_, i) => (
                    <td
                      key={i}
                      style={{
                        padding: "9px 14px",
                        background: NW.violetDark,
                        color: "#FFFFFF",
                        fontWeight: 800,
                        fontSize: 12,
                        fontFamily: THEME.fonts.heading,
                        border: `1px solid ${NW.violet}`,
                        borderRight:
                          i === rowColCount - 1
                            ? `3px solid ${NW.rose}`
                            : undefined,
                        textAlign: i === 0 ? "left" : "center",
                        position: "sticky",
                        left: i * DIM_COL_W,
                        zIndex: 10,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {i === 0 ? "∑ TOTAL GÉNÉRAL" : ""}
                    </td>
                  ))}
                  {grandTotals.map((val, di) => (
                    <td
                      key={di}
                      style={{
                        padding: "9px 14px",
                        textAlign: "right",
                        background: NW.violetDark,
                        color: NW.roseMed,
                        fontWeight: 700,
                        fontSize: 12,
                        border: `1px solid ${NW.violet}`,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmtNum(val)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      );

    // Grille vide décorative
    return (
      <div
        style={{
          position: "relative",
          minHeight: 360,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: NW.white,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: [
              `repeating-linear-gradient(0deg, ${NW.gray200} 0px, ${NW.gray200} 1px, transparent 1px, transparent 26px)`,
              `repeating-linear-gradient(90deg, ${NW.gray200} 0px, ${NW.gray200} 1px, transparent 1px, transparent 64px)`,
            ].join(", "),
            opacity: 0.35,
          }}
        />
        <div
          style={{
            position: "relative",
            textAlign: "center",
            color: NW.gray500,
            fontSize: 12,
            lineHeight: 1.8,
            padding: "0 28px",
          }}
        >
          Glissez des dimensions dans{" "}
          <strong style={{ color: NW.violet }}>↕ Lignes</strong> et{" "}
          <strong style={{ color: NW.rose }}>↔ Colonnes</strong>, sélectionnez
          une <strong style={{ color: NW.amber }}>Mesure</strong> puis cliquez
          sur <strong>Exécuter</strong>.
        </div>
      </div>
    );
  };

  return (
    <>
      {/* FIX ③ : keyframes animation table */}
      <style>{`
        @keyframes nwFadeSlideIn {
          from { opacity: 0; transform: translateY(12px) scale(0.99); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        @keyframes nwRunPulse {
          0%   { box-shadow: 0 4px 14px rgba(24,75,140,0.25); }
          40%  { box-shadow: 0 6px 28px rgba(24,75,140,0.55), 0 0 0 3px rgba(125,168,217,0.2); }
          100% { box-shadow: 0 4px 14px rgba(24,75,140,0.25); }
        }
        .nw-run-btn-active {
          animation: nwRunPulse 0.7s ease-out;
        }
        @keyframes nwDateShake {
          0%   { transform: translateX(0); }
          15%  { transform: translateX(-5px); }
          30%  { transform: translateX(5px); }
          45%  { transform: translateX(-4px); }
          60%  { transform: translateX(4px); }
          75%  { transform: translateX(-2px); }
          90%  { transform: translateX(2px); }
          100% { transform: translateX(0); }
        }
        .nw-date-shake {
          animation: nwDateShake 0.35s ease;
        }
      `}</style>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          fontFamily: THEME.fonts.body,
        }}
      >
        {/* Barre info (topN + filtre colonnes + compteur) */}
        {hasData && active && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 12,
              color: NW.gray500,
              flexWrap: "wrap",
            }}
          >
            {topN !== null && (
              <span style={{ color: NW.violet, fontWeight: 700 }}>
                🏆 Top {topN} / {apiRows.length} lignes
              </span>
            )}

            {/* Input recherche colonnes — FIX ① */}
            {colDims.length > 0 && (
              <div
                ref={colFilterRef}
                style={{ position: "relative", display: "inline-block" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 10px",
                    border: `1.5px solid ${colFilterOpen ? NW.rose : NW.gray200}`,
                    borderRadius: 8,
                    background: colFilterOpen ? NW.roseSoft : NW.white,
                    transition: "border-color 0.15s",
                    boxShadow: colFilterOpen
                      ? `0 0 0 2px ${NW.roseMed}`
                      : undefined,
                  }}
                >
                  <span style={{ color: NW.rose, fontSize: 13 }}>🔍</span>
                  <input
                    ref={colFilterInputRef}
                    type="text"
                    value={colFilterQuery}
                    onFocus={handleColFilterFocus}
                    onChange={(e) => {
                      setColFilterQuery(e.target.value);
                      setColFilterOpen(true);
                    }}
                    placeholder="Filtrer colonnes…"
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontSize: 12,
                      fontFamily: THEME.fonts.body,
                      color: NW.gray800,
                      width: 160,
                    }}
                  />
                  {colMembersSelected !== null && (
                    <span
                      style={{
                        background: NW.roseMed,
                        color: NW.roseDark,
                        borderRadius: 99,
                        fontSize: 11,
                        padding: "1px 8px",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {colMembersSelected.size}✓
                    </span>
                  )}
                  {(colFilterQuery || colMembersSelected !== null) && (
                    <button
                      onClick={() => {
                        // FIX ② : effacer le filtre ne vide PAS la table
                        setColFilterQuery("");
                        setColMembersSelected(null);
                        colFilterInputRef.current?.focus();
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: NW.gray500,
                        fontSize: 14,
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
                {colFilterOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      zIndex: 9999,
                      background: NW.white,
                      border: `1.5px solid ${NW.roseMed}`,
                      borderRadius: 10,
                      boxShadow: "0 8px 30px rgba(125,168,217,0.15)",
                      minWidth: 260,
                      maxHeight: 360,
                      overflowY: "auto",
                      padding: "8px 0",
                    }}
                  >
                    <div
                      style={{
                        padding: "6px 14px 8px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 12,
                          color: NW.roseDark,
                          flex: 1,
                        }}
                      >
                        ↔ Membres colonnes
                        {colDims.length > 0 && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 400,
                              color: NW.gray500,
                              marginLeft: 6,
                            }}
                          >
                            (
                            {colDims
                              .map((d) => d.hierarchyName ?? d.dimensionName)
                              .join(", ")}
                            )
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => {
                          setColMembersSelected(null);
                          setColFilterQuery("");
                        }}
                        style={{
                          fontSize: 10,
                          padding: "1px 7px",
                          border: `1px solid ${NW.gray200}`,
                          borderRadius: 4,
                          cursor: "pointer",
                          background: NW.white,
                          color: NW.gray700,
                        }}
                      >
                        Tout
                      </button>
                      <button
                        onClick={() => setColMembersSelected(new Set())}
                        style={{
                          fontSize: 10,
                          padding: "1px 7px",
                          border: `1px solid ${NW.gray200}`,
                          borderRadius: 4,
                          cursor: "pointer",
                          background: NW.white,
                          color: NW.gray700,
                        }}
                      >
                        Aucun
                      </button>
                    </div>
                    {colMembersLoading && (
                      <div
                        style={{
                          padding: "10px 14px",
                          color: NW.gray500,
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          className="spinner"
                          style={{ width: 14, height: 14 }}
                        />
                        Chargement des membres…
                      </div>
                    )}
                    {!colMembersLoading && searchedMembers.length === 0 && (
                      <div
                        style={{
                          padding: "10px 14px",
                          color: NW.gray500,
                          fontSize: 12,
                          fontStyle: "italic",
                        }}
                      >
                        Aucun résultat pour « {colFilterQuery} »
                      </div>
                    )}
                    {!colMembersLoading &&
                      searchedMembers.map((member) => {
                        const checked =
                          colMembersSelected === null ||
                          colMembersSelected.has(member);
                        return (
                          <label
                            key={member}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 9,
                              padding: "7px 14px",
                              cursor: "pointer",
                              fontSize: 12,
                              color: checked ? NW.roseDark : NW.gray500,
                              fontWeight: checked ? 600 : 400,
                              background: checked ? NW.roseSoft : "transparent",
                              transition: "background 0.1s",
                              borderBottom: `1px solid ${NW.gray100}`,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = NW.roseLight)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = checked
                                ? NW.roseSoft
                                : "transparent")
                            }
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleColMember(member)}
                              style={{
                                accentColor: NW.rose,
                                width: 14,
                                height: 14,
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {member}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                )}
              </div>
            )}

            <span style={{ marginLeft: "auto" }}>
              {filteredFlatRows.length} lignes · {allChildren.length} mesures
            </span>
          </div>
        )}

        {/* Conteneur principal avec drop zones toujours visibles */}
        <div
          style={{
            border: `1.5px solid ${NW.violetMed}`,
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: `0 4px 20px rgba(24,75,140,0.07)`,
          }}
        >
          {/* ↔ COLONNES */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setColOver(true);
            }}
            onDragLeave={() => setColOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setColOver(false);
              if (onDrop) onDrop("columns", null);
            }}
            style={{
              minHeight: 50,
              borderBottom: `2px dashed ${colOver ? NW.rose : NW.roseMed}`,
              background: colOver
                ? `${NW.rose}10`
                : `linear-gradient(90deg, ${NW.roseSoft} 0%, #FFFFFF 80%)`,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 18px",
              flexWrap: "wrap",
              transition: "border-color 0.15s, background 0.15s",
              cursor: "default",
            }}
          >
            <span
              style={{
                color: NW.rose,
                fontWeight: 700,
                fontSize: 12,
                whiteSpace: "nowrap",
                letterSpacing: 0.3,
              }}
            >
              ↔ Colonnes
            </span>
            {colDims.length === 0 ? (
              <span
                style={{
                  color: colOver ? NW.rose : NW.gray500,
                  fontSize: 11,
                  fontStyle: "italic",
                  transition: "color 0.15s",
                }}
              >
                ↑ Glissez une dimension ici
              </span>
            ) : (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {colDims.map((sel, i) => (
                  <span
                    key={i}
                    style={{
                      background: NW.rose,
                      color: "#FFFFFF",
                      borderRadius: 7,
                      padding: "4px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      boxShadow: "0 2px 6px rgba(125,168,217,0.25)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {sel.hierarchyName ?? sel.dimensionName}
                    {sel.levelName && sel.levelName !== sel.hierarchyName && (
                      <span style={{ fontSize: 10, opacity: 0.72 }}>
                        · {sel.levelName}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* LIGNE : ↕ LIGNES (gauche) | Contenu (droite) */}
          <div style={{ display: "flex", minHeight: 380 }}>
            {/* ↕ LIGNES */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setRowOver(true);
              }}
              onDragLeave={() => setRowOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setRowOver(false);
                if (onDrop) onDrop("rows", null);
              }}
              style={{
                width: 84,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                paddingTop: 36,
                gap: 8,
                borderRight: `2px dashed ${rowOver ? NW.violet : NW.violetMed}`,
                background: rowOver
                  ? `${NW.violet}10`
                  : `linear-gradient(180deg, ${NW.violetSoft} 0%, #FFFFFF 80%)`,
                transition: "border-color 0.15s, background 0.15s",
                cursor: "default",
              }}
            >
              <span
                style={{
                  writingMode: "vertical-rl",
                  textOrientation: "mixed",
                  transform: "rotate(180deg)",
                  color: NW.violet,
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: 0.3,
                  whiteSpace: "nowrap",
                }}
              >
                ↕ Lignes
              </span>
              {rowDims.length === 0 ? (
                <span
                  style={{
                    writingMode: "vertical-rl",
                    textOrientation: "mixed",
                    transform: "rotate(180deg)",
                    color: rowOver ? NW.violet : NW.gray500,
                    fontSize: 10,
                    fontStyle: "italic",
                    whiteSpace: "nowrap",
                    marginTop: 6,
                    transition: "color 0.15s",
                  }}
                >
                  ← Glissez une dimension ici
                </span>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                    alignItems: "center",
                    marginTop: 8,
                  }}
                >
                  {rowDims.map((sel, i) => (
                    <span
                      key={i}
                      style={{
                        background: NW.violet,
                        color: "#FFFFFF",
                        borderRadius: 7,
                        padding: "5px 9px",
                        fontSize: 10,
                        fontWeight: 700,
                        writingMode: "vertical-rl",
                        textOrientation: "mixed",
                        transform: "rotate(180deg)",
                        whiteSpace: "nowrap",
                        maxHeight: 130,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        boxShadow: "0 2px 6px rgba(24,75,140,0.25)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      {sel.hierarchyName ?? sel.dimensionName}
                      {sel.levelName && sel.levelName !== sel.hierarchyName && (
                        <span style={{ fontSize: 9, opacity: 0.7 }}>
                          {" "}
                          · {sel.levelName}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* CONTENU */}
            <div style={{ flex: 1, minWidth: 0, background: NW.white }}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Quick period helper
// ─────────────────────────────────────────────────────────────────────────────
function getQuickPeriod(t: "week" | "month" | "year"): {
  dateFrom: string;
  dateTo: string;
} {
  const d = new Date();
  const from = new Date(d);
  if (t === "week") from.setDate(d.getDate() - 7);
  else if (t === "month") from.setMonth(d.getMonth() - 1);
  else from.setFullYear(d.getFullYear() - 1);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: d.toISOString().slice(0, 10),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Démarque helper — construit les phases de démarque d'une période de solde
// ─────────────────────────────────────────────────────────────────────────────
interface DemarquePhase {
  label: string;
  icon: string;
  dateFrom: string;
  dateTo: string;
}

function buildDemarquePhases(p: {
  date_deb_priv: string | null;
  date_debut: string | null;
  date_debut2: string | null;
  date_debut3: string | null;
  date_debut4: string | null;
  date_fin: string | null;
}): DemarquePhase[] {
  const fin = p.date_fin ? p.date_fin.slice(0, 10) : "";
  const phases: DemarquePhase[] = [];
  if (p.date_debut)
    phases.push({
      label: "Démarque 1",
      icon: "🏷️",
      dateFrom: p.date_debut.slice(0, 10),
      dateTo: p.date_debut2 ? dayBefore(p.date_debut2) : fin,
    });
  if (p.date_debut2)
    phases.push({
      label: "Démarque 2",
      icon: "🏷️",
      dateFrom: p.date_debut2.slice(0, 10),
      dateTo: p.date_debut3 ? dayBefore(p.date_debut3) : fin,
    });
  if (p.date_debut3)
    phases.push({
      label: "Démarque 3",
      icon: "🏷️",
      dateFrom: p.date_debut3.slice(0, 10),
      dateTo: p.date_debut4 ? dayBefore(p.date_debut4) : fin,
    });
  if (p.date_debut4)
    phases.push({
      label: "Démarque 4",
      icon: "🏷️",
      dateFrom: p.date_debut4.slice(0, 10),
      dateTo: fin,
    });
  return phases;
}

// ─────────────────────────────────────────────────────────────────────────────
// AIQueryBuilder — composant principal (version IA)
// ─────────────────────────────────────────────────────────────────────────────
const AIQueryBuilder: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    dimensions,
    measures,
    loading: cubeLoading,
  } = useAppSelector((s) => s.cube);
  const {
    measures: selMeasures,
    rows,
    columns,
    dateFrom,
    dateTo,
    topN,
  } = useAppSelector((s) => s.query);

  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const dragPayload = useRef<DragPayload | null>(null);
  const [measureQuery, setMeasureQuery] = useState("");
  const [measureDropOpen, setMeasureDropOpen] = useState(false);
  const [dimPanelCollapsed, setDimPanelCollapsed] = useState(false);
  const measureInputRef = useRef<HTMLInputElement>(null);
  const measureDropRef = useRef<HTMLDivElement>(null);
  const [resultsActive, setResultsActive] = useState(false);

  // FIX ③ : clé d'animation, incrémentée à chaque "Exécuter"
  const [animKey, setAnimKey] = useState(0);
  // Pour l'animation du bouton Exécuter
  const [runBtnAnim, setRunBtnAnim] = useState(false);
  const [measurePopupGroup, setMeasurePopupGroup] = useState<string | null>(
    null,
  );
  // Snapshot des mesures sélectionnées à l'ouverture du popup (pour "Annuler")
  const [popupInitialMeasures, setPopupInitialMeasures] = useState<string[]>(
    [],
  );
  const [hoveredPlusGroup, setHoveredPlusGroup] = useState<string | null>(null);

  // ── AJOUT ⑥ : collapse du bloc Mesures ───────────────────────────────────
  const [measuresCollapsed, setMeasuresCollapsed] = useState(false);

  // ── AJOUT ⑦ : collapse du bloc Période / TopN / Solde ────────────────────
  const [controlsCollapsed, setControlsCollapsed] = useState(false);

  const measurePopupRef = useRef<HTMLDivElement>(null);
  // ── Enregistrer / charger requêtes sauvegardées ───────────────────────────
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveRefreshToken, setSaveRefreshToken] = useState(0);
  // ── Périodes de comparaison sauvegardées ─────────────────────────────────
  // Alimenté lors du chargement d'une requête sauvegardée, passé à ComparisonView
  const [savedComparisonPeriods, setSavedComparisonPeriods] = useState<Array<{
    dateFrom: string;
    dateTo: string;
  }> | null>(null);
  // Token incrémenté pour déclencher l'auto-run dans ComparisonView
  const [comparisonAutoRunToken, setComparisonAutoRunToken] = useState(0);

  // ── (Solde et Démarque supprimés dans cette version IA) ──────────────────

  useEffect(() => {
    dispatch(fetchDimensions());
    dispatch(fetchMeasures());
  }, [dispatch]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !measureInputRef.current?.contains(t) &&
        !measureDropRef.current?.contains(t)
      )
        setMeasureDropOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        measurePopupRef.current &&
        !measurePopupRef.current.contains(e.target as Node)
      )
        setMeasurePopupGroup(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const measureSuggestions = useMemo(() => {
    const q = measureQuery.trim().toLowerCase();
    if (!q) return measures.slice(0, 15);
    return measures.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        (m.caption ?? "").toLowerCase().includes(q) ||
        (m.measureGroup ?? "").toLowerCase().includes(q),
    );
  }, [measureQuery, measures]);

  const handleMeasureSelect = (name: string) => {
    dispatch(toggleMeasure(name));
    setResultsActive(false);
    setMeasureQuery("");
    setMeasureDropOpen(false);
    measureInputRef.current?.focus();
  };


  // FIX ② : ne plus appeler setResultsActive(false) → la table reste visible
  const handleRemoveFromRows = (i: number) => {
    dispatch(removeFromRows(i));
    // NE PAS vider la table : setResultsActive(false) retiré
  };

  const handleRemoveFromColumns = (i: number) => {
    dispatch(removeFromColumns(i));
    // NE PAS vider la table : setResultsActive(false) retiré
  };

  // FIX ③ : incrémenter animKey à chaque exécution
  const handleRunQuery = () => {
    dispatch(runQuery());
    setResultsActive(true);
    setAnimKey((k) => k + 1);
    // Animation bouton
    setRunBtnAnim(true);
    setTimeout(() => setRunBtnAnim(false), 700);
  };

  const handleLoadSavedQuery = (q: SavedQuery) => {
    dispatch(resetQuery());
    // Mesures
    q.measures.forEach((m: string) => dispatch(toggleMeasure(m)));
    // Dimensions lignes
    (q.rowsDimensions ?? []).forEach((d: any) => dispatch(addToRows(d)));
    // Dimensions colonnes
    (q.columnsDimensions ?? []).forEach((d: any) => dispatch(addToColumns(d)));
    // Période
    if (q.dateFrom || q.dateTo) {
      dispatch(
        setDateRange({ dateFrom: q.dateFrom ?? "", dateTo: q.dateTo ?? "" }),
      );
    }
    // Top N
    if (q.topN !== null && q.topN !== undefined) {
      dispatch(setTopN(q.topN));
    }

    // ── Périodes de comparaison : parser le JSON et injecter dans ComparisonView
    const rawPeriods = (q as any).comparisonPeriods;
    if (rawPeriods) {
      try {
        const parsed: Array<{ dateFrom: string; dateTo: string }> =
          typeof rawPeriods === "string" ? JSON.parse(rawPeriods) : rawPeriods;
        setSavedComparisonPeriods(Array.isArray(parsed) ? parsed : null);
      } catch {
        setSavedComparisonPeriods(null);
      }
    } else {
      setSavedComparisonPeriods(null);
    }

    // ── Basculer automatiquement vers la bonne vue selon la section sauvegardée
    const savedSection = (q as any).section as string | undefined;
    if (savedSection === "comparaison") {
      setViewMode("comparison");
      setComparisonAutoRunToken((t) => t + 1);
      return;
    }
    if (savedSection === "evolution") {
      setViewMode("evolution");
      return;
    }

    // ✅ Auto-exécution immédiate après chargement (vues table / dashboard)
    dispatch(runQuery());
    setResultsActive(true);
    setAnimKey((k) => k + 1);
    setRunBtnAnim(true);
    setTimeout(() => setRunBtnAnim(false), 700);
  };

  const handleDrop = (zone: "rows" | "columns", targetIdx: number | null) => {
    const p = dragPayload.current;
    if (!p) return;
    if (p.source === zone && p.index !== undefined && targetIdx !== null) {
      if (zone === "rows")
        dispatch(reorderRows({ from: p.index, to: targetIdx }));
      else dispatch(reorderColumns({ from: p.index, to: targetIdx }));
    } else if (p.source !== zone) {
      if (zone === "rows") dispatch(moveToRows(p.sel));
      else dispatch(moveToColumns(p.sel));
      setResultsActive(false);
    }
    dragPayload.current = null;
  };

  const measureGroups = measures.reduce<Record<string, typeof measures>>(
    (acc, m) => {
      (acc[m.measureGroup] = acc[m.measureGroup] || []).push(m);
      return acc;
    },
    {},
  );

  const dims = dimensions.filter((d) => d.name !== "Measures");
  const hasConfig =
    selMeasures.length > 0 && (rows.length > 0 || columns.length > 0);

  const viewTabs: { key: ViewMode; icon: string; label: string }[] = [
    { key: "table", icon: "⊞", label: "Tableau" },
    { key: "dashboard", icon: "◫", label: "Dashboard" },
  ];

  const card = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: NW.white,
    borderRadius: 12,
    border: `1px solid ${NW.gray200}`,
    boxShadow: "0 1px 8px rgba(24,75,140,0.06)",
    ...extra,
  });
  const inputStyle: React.CSSProperties = {
    border: `1.5px solid ${NW.gray200}`,
    borderRadius: 8,
    padding: "5px 10px",
    fontSize: 12,
    fontFamily: THEME.fonts.body,
    color: NW.gray800,
    outline: "none",
    background: NW.white,
    transition: "border-color 0.15s",
  };

  // ── IA : état du bouton "Remplir par IA" ─────────────────────────────────
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiError, setAiError] = React.useState<string | null>(null);
  const [aiApplied, setAiApplied] = React.useState(false);
 const [showPrevisionView, setShowPrevisionView] = React.useState(false);
const [previsionData, setPrevisionData] = React.useState<Record<string, Record<string, number>>>({});
const [previsionLoading, setPrevisionLoading] = React.useState(false);
const [previsionFamilles, setPrevisionFamilles] = React.useState<string[]>([]);
  // Dépôts servis par le modèle (GET /prevision-f/depots)
  const { depots } = useForecastDepots();

  
  const aiInitDone = React.useRef(false);

  // ── Pré-sélection automatique au montage ─────────────────────────────────
  // Dès que les dimensions et mesures sont chargées, on applique la config par défaut :
  //   Lignes → Cod Dep, Sousfamille, Famille
  //   Mesure → Vente PTTC (ou première mesure contenant "pttc")
  //   Période → mois courant (1er → aujourd'hui)
  React.useEffect(() => {
    if (aiInitDone.current) return;
    if (dimensions.length === 0 || measures.length === 0) return;
    aiInitDone.current = true;

    // 1. Reset propre
    dispatch(resetQuery());

    // 3. Mesure Vente PTTC
    const ventePttc =
      measures.find((m) => {
        const cap = (m.caption ?? m.name).toLowerCase();
        const grp = (m.measureGroup ?? "").toLowerCase();
        return (
          (cap.includes("pttc") || cap.includes("vente pttc")) &&
          (grp.includes("vente") || cap.includes("vente"))
        );
      }) ??
      measures.find((m) =>
        (m.caption ?? m.name).toLowerCase().includes("pttc"),
      );
    if (ventePttc) dispatch(toggleMeasure(ventePttc.name));

    // 4. Période : garder la date existante, sinon mois courant par défaut
    if (!dateFrom && !dateTo) {
      const now = new Date();
      const y = now.getFullYear();
      const mo = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      dispatch(
        setDateRange({ dateFrom: `${y}-${mo}-01`, dateTo: `${y}-${mo}-${dd}` }),
      );
    }

    setAiApplied(true);
    // ✅ Auto-run immédiat au montage après pré-sélection
    setTimeout(() => {
      dispatch(runQuery());
      setResultsActive(true);
      setAnimKey((k) => k + 1);
      setRunBtnAnim(true);
      setTimeout(() => setRunBtnAnim(false), 700);
    }, 150);
  }, [dimensions, measures]);

 const handleAIFill = () => {
  const savedDateFrom = dateFrom;
  const savedDateTo = dateTo;

  dispatch(resetQuery());

  // 1. Famille en lignes
  const filteredDims = dimensions.filter((d) => d.name !== "Measures");
  const familleTarget = filteredDims.find((d) =>
    d.name.toLowerCase().replace(/\s+/g, "").includes("famille") &&
    !d.name.toLowerCase().replace(/\s+/g, "").includes("sousfamille")
  );
  if (familleTarget) {
    const h = familleTarget.hierarchies[0];
    if (h) {
      const levels = h.levels ?? [];
      const levelName = levels.filter((l) => l !== "(All)").at(-1) ?? levels.at(-1) ?? "";
      dispatch(addToRows({ dimensionName: familleTarget.name, hierarchyName: h.name, levelName }));
    }
  }

  // 2. Cod Dep en colonnes
  const depotTarget = filteredDims.find((d) => {
    const name = d.name.toLowerCase().replace(/\s+/g, "");
    return name.includes("depot") || name.includes("dep");
  });
  if (depotTarget) {
    for (const h of depotTarget.hierarchies) {
      if (h.name.toLowerCase().includes("cod dep") || h.name.toLowerCase().includes("codedep")) {
        const levels = h.levels ?? [];
        const levelName = levels.filter((l) => l !== "(All)").at(-1) ?? levels.at(-1) ?? "";
        dispatch(addToColumns({ dimensionName: depotTarget.name, hierarchyName: h.name, levelName }));
        break;
      }
    }
  }

  // 3. Mesure Vente PTTC
  const ventePttc = measures.find((m) => {
    const cap = (m.caption ?? m.name).toLowerCase();
    const grp = (m.measureGroup ?? "").toLowerCase();
    return cap.includes("pttc") && grp.includes("vente");
  }) ?? measures.find((m) => (m.caption ?? m.name).toLowerCase().includes("pttc"));
  if (ventePttc) dispatch(toggleMeasure(ventePttc.name));

  // 4. Restaurer les dates
  if (savedDateFrom || savedDateTo) {
    dispatch(setDateRange({ dateFrom: savedDateFrom, dateTo: savedDateTo }));
  } else {
    const now = new Date();
    const y = now.getFullYear();
    const mo = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    dispatch(setDateRange({ dateFrom: `${y}-${mo}-01`, dateTo: `${y}-${mo}-${dd}` }));
  }

  setAiApplied(true);
setAiError(null);
setShowPrevisionView(true);
setPrevisionLoading(true);
setPrevisionData({});
setPrevisionFamilles([]);


getPrevisionFamilles().then(async (r) => {
  const familles = r.data.map((f: any) => f.codFamp ?? f.codFam ?? f.designation ?? "");
  setPrevisionFamilles(familles);
  const result: Record<string, Record<string, number>> = {};
  for (const famille of familles) {
    result[famille] = {};
    for (const depot of depots) {
      try {
        const res = await (postPrevisionF as any)({
          dimension: "famille",
          labelCode: famille,
          dateFrom: savedDateFrom || `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,"0")}-01`,
          dateTo: savedDateTo || new Date().toISOString().slice(0,10),
          depot,
        });
        if (res.data?.success && res.data?.forecastCa) {
          result[famille][depot] = res.data.forecastCa;
        }
      } catch { }
    }
    setPrevisionData({ ...result });
  }
  setPrevisionLoading(false);
});
};
  // ── Display values for masked date inputs (DD/MM/YYYY) ────────────────────
  const [dateFromDisplay, setDateFromDisplay] = React.useState(() =>
    isoToDisplay(dateFrom),
  );
  const [dateToDisplay, setDateToDisplay] = React.useState(() =>
    isoToDisplay(dateTo),
  );
  React.useEffect(() => {
    setDateFromDisplay(isoToDisplay(dateFrom));
  }, [dateFrom]);
  React.useEffect(() => {
    setDateToDisplay(isoToDisplay(dateTo));
  }, [dateTo]);

  // ── Error + shake state for invalid date inputs ───────────────────────────
  const [dateFromError, setDateFromError] = React.useState(false);
  const [dateToError, setDateToError] = React.useState(false);
  const dateFromRef = React.useRef<HTMLInputElement>(null);
  const dateToRef = React.useRef<HTMLInputElement>(null);

  const triggerShake = (
    ref: React.RefObject<HTMLInputElement | null>,
    setError: (v: boolean) => void,
  ) => {
    setError(true);
    ref.current?.classList.remove("nw-date-shake");
    void ref.current?.offsetWidth; // reflow to restart animation
    ref.current?.classList.add("nw-date-shake");
    setTimeout(() => {
      ref.current?.classList.remove("nw-date-shake");
      setError(false);
    }, 400);
  };

  return (
    // ══ LAYOUT PRINCIPAL : SIDEBAR DIMENSIONS (gauche) + CONTENU (droite) ══
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 14,
        fontFamily: THEME.fonts.body,
        background: NW.gray50,
        padding: "2px 0",
        alignItems: "flex-start",
      }}
    >
      {/* ══ SIDEBAR DIMENSIONS (verticale, gauche) ════════════════════════ */}
      <div
        style={{
          ...card({ overflow: "visible" }),
          width: dimPanelCollapsed ? "auto" : 260,
          flexShrink: 0,
          alignSelf: "flex-start",
          position: "sticky",
          top: 0,
        }}
      >
        {/* Header sidebar */}
        <div
          style={{
            padding: "12px 14px",
            background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 50%, #1D5BA8 100%)`,
            borderRadius: dimPanelCollapsed ? 12 : "12px 12px 0 0",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 16, color: "rgba(255,255,255,0.8)" }}>
            ⬡
          </span>
          {!dimPanelCollapsed && (
            <span
              style={{
                fontFamily: THEME.fonts.heading,
                fontWeight: 700,
                fontSize: 13,
                color: "#FFFFFF",
              }}
            >
              Dimensions
            </span>
          )}
          {cubeLoading && (
            <span className="spinner" style={{ marginLeft: 4 }} />
          )}
          {!dimPanelCollapsed && (
            <span
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.45)",
                marginTop: 2,
              }}
            >
              {dimensions
                .filter((d) => d.name !== "Measures")
                .reduce((n, d) => n + d.hierarchies.length, 0)}{" "}
              hiérarchies
            </span>
          )}
          {/* Bouton collapse Dimensions — existant */}
          <CollapseToggleBtn
            collapsed={dimPanelCollapsed}
            onToggle={() => setDimPanelCollapsed((v) => !v)}
            title={
              dimPanelCollapsed
                ? "Afficher les dimensions"
                : "Masquer les dimensions"
            }
          />
        </div>

        {/* Corps sidebar — hiérarchies regroupées par dimension
            (limité aux dimensions utilisées par la prévision IA) */}
        {!dimPanelCollapsed && (
          <DimensionPanel
            dimensions={dims.filter((d) => {
              const name = d.name.toLowerCase().replace(/\s+/g, "");
              return (
                name.includes("depot") ||
                name.includes("dep") ||
                name.includes("famille") ||
                name.includes("sousfamille")
              );
            })}
            rows={rows}
            columns={columns}
            loading={cubeLoading}
            onAdd={(sel, target) => {
              if (target === "rows") dispatch(addToRows(sel));
              else dispatch(addToColumns(sel));
              setResultsActive(false);
            }}
            onDragStart={(sel) => {
              dragPayload.current = { source: "list", sel };
            }}
          />
        )}
      </div>

      {/* ══ DROITE : MESURES + CONTRÔLES + RÉSULTATS ══════════════════════ */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* ── BLOC MESURES ─────────────────────────────────────────────────── */}
        {/* ⑥ : le header reste toujours visible ; le corps se cache si measuresCollapsed */}
        <div style={card({ overflow: "visible" })}>
          {/* Header Mesures */}
          <div
            style={{
              padding: "12px 18px",
              background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 50%, #1D5BA8 100%)`,
              borderRadius: measuresCollapsed ? 12 : "12px 12px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              transition: "border-radius 0.2s",
            }}
          >
            <span style={{ color: "#FCE7BF", fontSize: 14 }}>◈</span>
            <span
              style={{
                fontFamily: THEME.fonts.heading,
                fontWeight: 700,
                fontSize: 13,
                color: "#FFFFFF",
              }}
            >
              Mesures
            </span>

            {/* Badge IA pré-configuré */}
            {aiApplied && (
              <span
                style={{
                  background: "rgba(29,91,168,0.35)",
                  border: "1px solid rgba(125,168,217,0.5)",
                  color: "#FCE7BF",
                  borderRadius: 99,
                  padding: "2px 9px",
                  fontSize: 10,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                ✨ IA configuré
              </span>
            )}

            {/* Chips mesures sélectionnées — toujours visibles même si collapsed */}
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 5,
                overflowX: "auto",
                maxWidth: 400,
                scrollbarWidth: "none",
                flexShrink: 1,
              }}
            >
              {selMeasures.map((name) => {
                const m = measures.find((x) => x.name === name);
                return (
                  <span
                    key={name}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 9px",
                      borderRadius: 99,
                      background: "rgba(125,168,217,0.22)",
                      border: "1px solid rgba(125,168,217,0.45)",
                      color: NW.roseLight,
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {m?.caption ?? name}
                    <button
 onClick={() => {
  dispatch(toggleMeasure(name));
  setResultsActive(false);
  setShowPrevisionView(false);
  setPrevisionData({});
  setPrevisionFamilles([]);
}}
  style={{
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.75)",
    cursor: "pointer",
    padding: 0,
    fontSize: 14,
    lineHeight: 1,
  }}
>
  ×
</button>
                  </span>
                );
              })}
            </div>

            {/* Input ajout mesure */}
            <div style={{ position: "relative", marginLeft: 4 }}>
              <input
                ref={measureInputRef}
                type="text"
                value={measureQuery}
                onChange={(e) => {
                  setMeasureQuery(e.target.value);
                  setMeasureDropOpen(true);
                }}
                onFocus={() => setMeasureDropOpen(true)}
                placeholder="◈ Ajouter une mesure…"
                style={{
                  background: "rgba(255,255,255,0.1)",
                  border: `1px solid ${measureDropOpen ? NW.rose : "rgba(255,255,255,0.2)"}`,
                  borderRadius: 99,
                  padding: "5px 14px",
                  fontSize: 12,
                  color: "#FFFFFF",
                  fontFamily: THEME.fonts.body,
                  outline: "none",
                  width: 200,
                  transition: "border-color 0.15s",
                }}
              />
              {measureDropOpen && measureSuggestions.length > 0 && (
                <div
                  ref={measureDropRef}
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: 0,
                    background: NW.white,
                    borderRadius: 10,
                    boxShadow: "0 8px 30px rgba(24,75,140,0.15)",
                    minWidth: 290,
                    maxHeight: 300,
                    overflowY: "auto",
                    zIndex: 999,
                    border: `1.5px solid ${NW.violetMed}`,
                  }}
                >
                  {measureSuggestions.map((m) => {
                    const active = selMeasures.includes(m.name);
                    return (
                      <div
                        key={m.name}
                        onClick={() => handleMeasureSelect(m.name)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "9px 14px",
                          cursor: "pointer",
                          background: active ? NW.roseLight : NW.white,
                          borderBottom: `1px solid ${NW.gray100}`,
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLElement).style.background =
                            active ? NW.roseMed : NW.violetSoft)
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLElement).style.background =
                            active ? NW.roseLight : NW.white)
                        }
                      >
                        <span
                          style={{
                            width: 18,
                            textAlign: "center",
                            fontWeight: 800,
                            fontSize: 13,
                            color: active ? NW.rose : NW.gray500,
                            flexShrink: 0,
                          }}
                        >
                          {active ? "✓" : "+"}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: active ? 700 : 500,
                              fontSize: 12,
                              color: active ? NW.roseDark : NW.gray800,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {m.caption ?? m.name}
                          </div>
                          {m.measureGroup && (
                            <div style={{ fontSize: 10, color: NW.gray500 }}>
                              {m.measureGroup}
                            </div>
                          )}
                        </div>
                        {active && (
                          <span
                            style={{
                              background: NW.rose,
                              color: "#FFFFFF",
                              borderRadius: 99,
                              fontSize: 10,
                              padding: "1px 7px",
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            Sélectionnée
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selMeasures.length > 0 && (
              <span
                style={{
                  background: "rgba(125,168,217,0.18)",
                  color: NW.roseLight,
                  border: "1px solid rgba(125,168,217,0.32)",
                  borderRadius: 99,
                  padding: "1px 9px",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {selMeasures.length} mesure{selMeasures.length > 1 ? "s" : ""}
              </span>
            )}

            {/* ⑥ Bouton collapse Mesures */}
            <CollapseToggleBtn
              collapsed={measuresCollapsed}
              onToggle={() => setMeasuresCollapsed((v) => !v)}
              title={
                measuresCollapsed
                  ? "Afficher les groupes de mesures"
                  : "Masquer les groupes de mesures"
              }
            />
          </div>

          {/* Corps Mesures : groupes VENTE / ACHAT — masqué si measuresCollapsed */}
          {!measuresCollapsed && (
            <div
              style={{
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {Object.entries(measureGroups)
                .filter(([group]) => group.toUpperCase() === "VENTE")
                .map(([group, ms]) => {
                  const PINNED_EXACT = ["pttc"];
                  const pinned = ms.filter((m) => {
                    const cap = (m.caption ?? m.name).trim().toLowerCase();
                    const base = cap
                      .replace(/\s*-\s*(achat|vente)$/, "")
                      .trim();
                    return PINNED_EXACT.includes(base);
                  });
                  const others = ms.filter((m) => !pinned.includes(m));
                  const isPopupOpen = measurePopupGroup === group;
                  const extraSelected = others.filter((m) =>
                    selMeasures.includes(m.name),
                  );
                  const groupCount = selMeasures.filter((n) =>
                    ms.some((m) => m.name === n),
                  ).length;

                  return (
                    <div
                      key={group}
                      style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 8px",
                        borderRadius: 8,
                        background: NW.gray50,
                        border: `1px solid ${NW.gray200}`,
                      }}
                    >
                      {/* ── Étiquette groupe (à gauche, largeur fixe) ── */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          minWidth: 64,
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            color: NW.violet,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            fontFamily: THEME.fonts.heading,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {group}
                        </span>
                        {groupCount > 0 && (
                          <span
                            style={{
                              background: NW.roseMed,
                              color: NW.roseDark,
                              borderRadius: 99,
                              padding: "0 6px",
                              fontSize: 10,
                              fontWeight: 700,
                              marginTop: 2,
                            }}
                          >
                            {groupCount}✓
                          </span>
                        )}
                      </div>

                      {/* ── Séparateur vertical ── */}
                      <div
                        style={{
                          width: 1.5,
                          alignSelf: "stretch",
                          background: NW.violetMed,
                          borderRadius: 1,
                          flexShrink: 0,
                        }}
                      />

                      {/* ── Chips (inline) ── */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 5,
                          alignItems: "center",
                          flex: 1,
                        }}
                      >
                        {/* Chips épinglées (Qte, Pttc, Remise, Tva, Phtarem) */}
                        {pinned.map((m) => {
                          const checked = selMeasures.includes(m.name);
                          return (
                            <button
                              key={m.name}
                              onClick={() => {
                                dispatch(toggleMeasure(m.name));
                                setResultsActive(false);
                              }}
                              style={{
                                padding: "5px 11px",
                                borderRadius: 8,
                                border: `1.5px solid ${checked ? NW.rose : NW.gray200}`,
                                background: checked ? NW.rose : NW.white,
                                color: checked ? "#FFFFFF" : NW.gray700,
                                fontSize: 11,
                                fontWeight: checked ? 700 : 500,
                                cursor: "pointer",
                                transition: "all 0.15s",
                                boxShadow: checked
                                  ? "0 2px 8px rgba(125,168,217,0.28)"
                                  : "0 1px 3px rgba(15,23,42,0.06)",
                                fontFamily: THEME.fonts.body,
                              }}
                            >
                              {m.caption ?? m.name}
                            </button>
                          );
                        })}

                        {/* Chips pour les mesures extra sélectionnées */}
                        {extraSelected.map((m) => (
                          <button
                            key={m.name}
                            onClick={() => {
                              dispatch(toggleMeasure(m.name));
                              setResultsActive(false);
                            }}
                            style={{
                              padding: "5px 9px",
                              borderRadius: 8,
                              border: `1.5px solid ${NW.violet}`,
                              background: NW.violetLight,
                              color: NW.violetDark,
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontFamily: THEME.fonts.body,
                              transition: "all 0.15s",
                            }}
                          >
                            {m.caption ?? m.name}
                            <span
                              style={{
                                fontSize: 14,
                                lineHeight: 1,
                                color: NW.violet,
                                opacity: 0.7,
                              }}
                            >
                              ×
                            </span>
                          </button>
                        ))}

                        {/* Bouton "+" */}
                        {others.length > 0 && (
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <button
                              onClick={() => {
                                if (!isPopupOpen)
                                  setPopupInitialMeasures([...selMeasures]);
                                setMeasurePopupGroup(
                                  isPopupOpen ? null : group,
                                );
                              }}
                              onMouseEnter={() => setHoveredPlusGroup(group)}
                              onMouseLeave={() => setHoveredPlusGroup(null)}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 8,
                                border: `1.5px solid ${isPopupOpen ? NW.violet : NW.gray200}`,
                                background: isPopupOpen ? NW.violet : NW.white,
                                color: isPopupOpen ? "#FFFFFF" : NW.violet,
                                fontSize: 18,
                                fontWeight: 700,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: 1,
                                transition: "all 0.15s",
                                boxShadow: isPopupOpen
                                  ? `0 2px 8px rgba(24,75,140,0.30)`
                                  : "0 1px 3px rgba(15,23,42,0.06)",
                                flexShrink: 0,
                              }}
                            >
                              +
                            </button>

                            {/* Tooltip */}
                            {hoveredPlusGroup === group && !isPopupOpen && (
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "calc(100% + 7px)",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  background: NW.gray800,
                                  color: "#FFFFFF",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: "5px 10px",
                                  borderRadius: 7,
                                  whiteSpace: "nowrap",
                                  pointerEvents: "none",
                                  zIndex: 10000,
                                  boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
                                  fontFamily: THEME.fonts.body,
                                }}
                              >
                                Ajouter une mesure {group}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: 0,
                                    height: 0,
                                    borderLeft: "5px solid transparent",
                                    borderRight: "5px solid transparent",
                                    borderTop: `5px solid ${NW.gray800}`,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ── Modal popup "autres mesures" ── */}
                      {isPopupOpen && (
                        <>
                          <div
                            onClick={() => setMeasurePopupGroup(null)}
                            style={{
                              position: "fixed",
                              inset: 0,
                              background: "rgba(13,42,80,0.35)",
                              backdropFilter: "blur(2px)",
                              zIndex: 9998,
                            }}
                          />
                          <div
                            ref={measurePopupRef}
                            style={{
                              position: "fixed",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              zIndex: 9999,
                              background: NW.white,
                              borderRadius: 16,
                              boxShadow:
                                "0 24px 80px rgba(24,75,140,0.22), 0 4px 20px rgba(15,23,42,0.12)",
                              width: 340,
                              maxHeight: "70vh",
                              display: "flex",
                              flexDirection: "column",
                              overflow: "hidden",
                              animation:
                                "nwFadeSlideIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
                            }}
                          >
                            <div
                              style={{
                                padding: "14px 18px",
                                background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span style={{ color: "#FCE7BF", fontSize: 16 }}>
                                ◈
                              </span>
                              <span
                                style={{
                                  flex: 1,
                                  fontFamily: THEME.fonts.heading,
                                  fontWeight: 700,
                                  fontSize: 13,
                                  color: "#FFFFFF",
                                }}
                              >
                                {group} — autres mesures
                              </span>
                            </div>
                            <div
                              style={{
                                overflowY: "auto",
                                flex: 1,
                                padding: "6px 0",
                              }}
                            >
                              {others.map((m) => {
                                const checked = selMeasures.includes(m.name);
                                return (
                                  <label
                                    key={m.name}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                      padding: "10px 18px",
                                      cursor: "pointer",
                                      background: checked
                                        ? NW.roseLight
                                        : "transparent",
                                      borderBottom: `1px solid ${NW.gray100}`,
                                      fontSize: 13,
                                      transition: "background 0.1s",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!checked)
                                        (
                                          e.currentTarget as HTMLElement
                                        ).style.background = NW.violetSoft;
                                    }}
                                    onMouseLeave={(e) => {
                                      (
                                        e.currentTarget as HTMLElement
                                      ).style.background = checked
                                        ? NW.roseLight
                                        : "transparent";
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => {
                                        dispatch(toggleMeasure(m.name));
                                        setResultsActive(false);
                                      }}
                                      style={{
                                        accentColor: NW.rose,
                                        width: 15,
                                        height: 15,
                                      }}
                                    />
                                    <span
                                      style={{
                                        color: checked
                                          ? NW.roseDark
                                          : NW.gray700,
                                        fontWeight: checked ? 700 : 500,
                                        fontFamily: THEME.fonts.body,
                                      }}
                                    >
                                      {m.caption ?? m.name}
                                    </span>
                                    {checked && (
                                      <span
                                        style={{
                                          marginLeft: "auto",
                                          background: NW.rose,
                                          color: "#FFFFFF",
                                          borderRadius: 99,
                                          fontSize: 10,
                                          padding: "1px 7px",
                                          fontWeight: 700,
                                        }}
                                      >
                                        ✓
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                padding: "10px 14px",
                                background: NW.gray50,
                                borderTop: `1px solid ${NW.gray200}`,
                                justifyContent: "flex-end",
                              }}
                            >
                              <button
                                onClick={() => {
                                  others.forEach((m) => {
                                    const wasChecked =
                                      popupInitialMeasures.includes(m.name);
                                    const isChecked = selMeasures.includes(
                                      m.name,
                                    );
                                    if (wasChecked !== isChecked)
                                      dispatch(toggleMeasure(m.name));
                                  });
                                  setMeasurePopupGroup(null);
                                }}
                                style={{
                                  padding: "7px 16px",
                                  borderRadius: 8,
                                  border: `1.5px solid ${NW.danger}`,
                                  background: NW.white,
                                  color: NW.danger,
                                  fontWeight: 700,
                                  fontSize: 12,
                                  fontFamily: THEME.fonts.body,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                }}
                              >
                                ✕ Annuler
                              </button>
                              <button
                                onClick={() => setMeasurePopupGroup(null)}
                                style={{
                                  padding: "7px 18px",
                                  borderRadius: 8,
                                  border: "none",
                                  background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                                  color: "#FFFFFF",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  fontFamily: THEME.fonts.body,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                  boxShadow: "0 2px 8px rgba(24,75,140,0.25)",
                                }}
                              >
                                ✓ Valider
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* ── BLOC CONTRÔLES (Période + TopN + Solde) ──────────────────────── */}
        {/* ⑦ : le header reste visible ; le corps disparaît si controlsCollapsed */}
        <div style={card({ overflow: "visible" })}>
          {/* Header contrôles */}
          <div
            style={{
              padding: "10px 18px",
              background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 50%, #1D5BA8 100%)`,
              borderRadius: controlsCollapsed ? 12 : "12px 12px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              transition: "border-radius 0.2s",
            }}
          >
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}>
              📅
            </span>
            <span
              style={{
                fontFamily: THEME.fonts.heading,
                fontWeight: 700,
                fontSize: 13,
                color: "#FFFFFF",
              }}
            >
              Période &amp; Filtres
            </span>

            {/* Résumé compact affiché même quand collapsed */}
            {controlsCollapsed && (dateFrom || dateTo || topN !== null) && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(dateFrom || dateTo) && (
                  <span
                    style={{
                      background: "rgba(255,255,255,0.18)",
                      color: "#FFFFFF",
                      borderRadius: 99,
                      padding: "2px 10px",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {dateFrom || "…"} → {dateTo || "…"}
                  </span>
                )}
                {topN !== null && (
                  <span
                    style={{
                      background: "rgba(252,231,191,0.25)",
                      color: "#FCE7BF",
                      borderRadius: 99,
                      padding: "2px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    🏆 Top {topN}
                  </span>
                )}
              </div>
            )}

            {/* ⑦ Bouton collapse Contrôles */}
            <CollapseToggleBtn
              collapsed={controlsCollapsed}
              onToggle={() => setControlsCollapsed((v) => !v)}
              title={
                controlsCollapsed
                  ? "Afficher les filtres"
                  : "Masquer les filtres"
              }
            />
          </div>

          {/* Corps contrôles — masqué si controlsCollapsed */}
          {!controlsCollapsed && (
            <div style={{ padding: "13px 18px" }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 14,
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: THEME.fonts.heading,
                      fontWeight: 700,
                      fontSize: 12,
                      color: NW.gray800,
                    }}
                  >
                    📅 Période
                  </span>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      color: NW.gray500,
                    }}
                  >
                    De :
                    <input
                      ref={dateFromRef}
                      type="text"
                      inputMode="numeric"
                      placeholder="JJ/MM/AAAA"
                      maxLength={10}
                      value={dateFromDisplay}
                      onChange={(e) => {
                        const masked = applyDateMask(e.target.value);
                        setDateFromDisplay(masked);
                        if (dateFromError) setDateFromError(false);
                        if (masked.length === 10) {
                          const iso = displayToIso(masked);
                          const fixed = fixYear(iso);
                          if (isValidDate(fixed)) {
                            const today = new Date().toISOString().slice(0, 10);
                            const capped = fixed > today ? today : fixed;
                            dispatch(
                              setDateRange({
                                dateFrom: capped,
                                dateTo: dateTo && capped > dateTo ? "" : dateTo,
                              }),
                            );
                          }
                        }
                      }}
                      onBlur={() => {
                        if (dateFromDisplay.length === 10) {
                          const iso = displayToIso(dateFromDisplay);
                          const fixed = fixYear(iso);
                          if (isValidDate(fixed)) {
                            const today = new Date().toISOString().slice(0, 10);
                            const capped = fixed > today ? today : fixed;
                            dispatch(
                              setDateRange({
                                dateFrom: capped,
                                dateTo: dateTo && capped > dateTo ? "" : dateTo,
                              }),
                            );
                          } else {
                            triggerShake(dateFromRef, setDateFromError);
                            dispatch(setDateRange({ dateFrom: "", dateTo }));
                          }
                          setDateFromDisplay(isoToDisplay(dateFrom));
                        } else if (dateFromDisplay === "") {
                          dispatch(setDateRange({ dateFrom: "", dateTo }));
                        } else if (dateFromDisplay.length > 0) {
                          triggerShake(dateFromRef, setDateFromError);
                          setDateFromDisplay(isoToDisplay(dateFrom));
                        }
                      }}
                      style={{
                        ...inputStyle,
                        width: 110,
                        letterSpacing: 0.5,
                        borderColor: dateFromError
                          ? NW.danger
                          : dateFromDisplay.length > 0 &&
                              dateFromDisplay.length < 10
                            ? NW.amber
                            : dateFrom
                              ? NW.violet
                              : NW.gray200,
                        boxShadow: dateFromError
                          ? `0 0 0 2px ${NW.danger}33`
                          : undefined,
                      }}
                    />
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      color: NW.gray500,
                    }}
                  >
                    À :
                    <input
                      ref={dateToRef}
                      type="text"
                      inputMode="numeric"
                      placeholder="JJ/MM/AAAA"
                      maxLength={10}
                      value={dateToDisplay}
                      onChange={(e) => {
                        const masked = applyDateMask(e.target.value);
                        setDateToDisplay(masked);
                        if (dateToError) setDateToError(false);
                        if (masked.length === 10) {
                          const iso = displayToIso(masked);
                          const fixed = fixYear(iso);
                          if (isValidDate(fixed)) {
                            const today = new Date().toISOString().slice(0, 10);
                            const capped = fixed > today ? today : fixed;
                            if (!dateFrom || capped >= dateFrom) {
                              dispatch(
                                setDateRange({ dateFrom, dateTo: capped }),
                              );
                            }
                          }
                        }
                      }}
                      onBlur={() => {
                        if (dateToDisplay.length === 10) {
                          const iso = displayToIso(dateToDisplay);
                          const fixed = fixYear(iso);
                          if (isValidDate(fixed)) {
                            const today = new Date().toISOString().slice(0, 10);
                            const capped = fixed > today ? today : fixed;
                            if (!dateFrom || capped >= dateFrom) {
                              dispatch(
                                setDateRange({ dateFrom, dateTo: capped }),
                              );
                            } else {
                              triggerShake(dateToRef, setDateToError);
                              dispatch(setDateRange({ dateFrom, dateTo: "" }));
                            }
                          } else {
                            triggerShake(dateToRef, setDateToError);
                            dispatch(setDateRange({ dateFrom, dateTo: "" }));
                          }
                          setDateToDisplay(isoToDisplay(dateTo));
                        } else if (dateToDisplay === "") {
                          dispatch(setDateRange({ dateFrom, dateTo: "" }));
                        } else if (dateToDisplay.length > 0) {
                          triggerShake(dateToRef, setDateToError);
                          setDateToDisplay(isoToDisplay(dateTo));
                        }
                      }}
                      style={{
                        ...inputStyle,
                        width: 110,
                        letterSpacing: 0.5,
                        borderColor: dateToError
                          ? NW.danger
                          : dateToDisplay.length > 0 &&
                              dateToDisplay.length < 10
                            ? NW.amber
                            : dateTo
                              ? NW.violet
                              : NW.gray200,
                        boxShadow: dateToError
                          ? `0 0 0 2px ${NW.danger}33`
                          : undefined,
                      }}
                    />
                  </label>
                  {(["week", "month", "year"] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => dispatch(setDateRange(getQuickPeriod(t)))}
                      style={{
                        fontSize: 11,
                        padding: "3px 11px",
                        borderRadius: 99,
                        cursor: "pointer",
                        border: `1px solid ${NW.violet}55`,
                        background: NW.violetLight,
                        color: NW.violet,
                        fontWeight: 700,
                        fontFamily: THEME.fonts.body,
                      }}
                    >
                      {t === "week" ? "7j" : t === "month" ? "1m" : "1an"}
                    </button>
                  ))}
                  {(dateFrom || dateTo) && (
                    <button
                      onClick={() =>
                        dispatch(setDateRange({ dateFrom: "", dateTo: "" }))
                      }
                      className="btn-ghost"
                      style={{ fontSize: 11, padding: "3px 10px" }}
                    >
                      ✕ Effacer
                    </button>
                  )}
                </div>
                <div
                  style={{
                    width: 1,
                    height: 28,
                    background: NW.gray200,
                    flexShrink: 0,
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      fontFamily: THEME.fonts.heading,
                      fontWeight: 700,
                      fontSize: 12,
                      color: NW.gray800,
                    }}
                  >
                    🏆 Top N
                  </span>
                  {[5, 10, 20, 50].map((n) => (
                    <button
                      key={n}
                      onClick={() => dispatch(setTopN(topN === n ? null : n))}
                      style={{
                        fontSize: 11,
                        padding: "3px 10px",
                        borderRadius: 99,
                        cursor: "pointer",
                        border: `1.5px solid ${topN === n ? NW.violet : NW.gray200}`,
                        background: topN === n ? NW.violet : NW.white,
                        color: topN === n ? "#FFFFFF" : NW.gray500,
                        fontWeight: topN === n ? 700 : 500,
                        transition: "all 0.15s",
                      }}
                    >
                      Top {n}
                    </button>
                  ))}
                  <input
                    type="number"
                    min={1}
                    max={9999}
                    value={
                      topN !== null && ![5, 10, 20, 50].includes(topN)
                        ? topN
                        : ""
                    }
                    placeholder="N"
                    onChange={(e) => {
                      const v = parseInt(e.target.value);
                      dispatch(setTopN(isNaN(v) || v < 1 ? null : v));
                    }}
                    style={{ ...inputStyle, width: 52 }}
                  />
                  {topN !== null && (
                    <button
                      onClick={() => dispatch(setTopN(null))}
                      className="btn-ghost"
                      style={{ fontSize: 11, padding: "3px 10px" }}
                    >
                      Tout
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions + onglets vue */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {/* ✨ Bouton Remplir par IA — configure ET exécute directement */}
          <button
            onClick={handleAIFill}
            disabled={cubeLoading}
            title="Claude IA analyse vos dimensions & mesures et configure automatiquement la requête optimale"
            style={{
              background: aiLoading
                ? NW.gray200
                : `linear-gradient(135deg, #1D5BA8, #7DA8D9)`,
              border: "none",
              borderRadius: 9,
              padding: "8px 18px",
              color: aiLoading ? NW.gray500 : "#FFFFFF",
              fontWeight: 700,
              fontSize: 13,
              cursor: aiLoading || cubeLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
              boxShadow: aiLoading
                ? "none"
                : `0 4px 18px rgba(29,91,168,0.35)`,
              fontFamily: THEME.fonts.body,
              transition: "all 0.2s",
              opacity: aiLoading || cubeLoading ? 0.6 : 1,
              whiteSpace: "nowrap",
            }}
          >
            ✨ Remplir par IA
          </button>

          {/* Message IA (explication ou erreur) */}
          {aiError && (
            <span
              style={{
                fontSize: 11,
                padding: "4px 12px",
                borderRadius: 99,
                background: aiError.startsWith("✓")
                  ? "#D3EFEA"
                  : NW.dangerLight,
                color: aiError.startsWith("✓") ? "#006357" : NW.danger,
                border: `1px solid ${aiError.startsWith("✓") ? "#A9DFD7" : NW.danger + "44"}`,
                fontWeight: 600,
                maxWidth: 340,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {aiError}
            </span>
          )}
          <button
            className="btn-ghost"
            onClick={() => {
              dispatch(resetQuery());
              setResultsActive(false);
            }}
            style={{
              fontSize: 12,
              padding: "7px 14px",
              borderRadius: 9,
              border: `1.5px solid ${NW.gray200}`,
              background: NW.white,
              color: NW.gray700,
              cursor: "pointer",
              fontFamily: THEME.fonts.body,
            }}
          >
            ↺ Reset
          </button>
          {!hasConfig && (
            <div style={{ display: "flex", gap: 6 }}>
              {selMeasures.length === 0 && (
                <span
                  style={{
                    background: NW.dangerLight,
                    color: NW.danger,
                    border: `1px solid ${NW.danger}33`,
                    borderRadius: 99,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  ⚠ Sélectionnez une mesure
                </span>
              )}
              {rows.length === 0 && columns.length === 0 && (
                <span
                  style={{
                    background: NW.amberLight,
                    color: NW.amber,
                    border: `1px solid ${NW.amber}33`,
                    borderRadius: 99,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  ⚠ Ajoutez une dimension
                </span>
              )}
            </div>
          )}
          {/* ── Onglets vue + Enregistrer + Requêtes sauvegardées ─────────────── */}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "nowrap",
            }}
          >
            {/* Bouton Enregistrer — actif seulement après un Exécuter */}
            <button
              onClick={() => setShowSaveModal(true)}
              disabled={!resultsActive}
              title={
                resultsActive
                  ? "Enregistrer cette requête"
                  : "Exécutez d'abord une requête"
              }
              style={{
                background: resultsActive
                  ? NW.violet
                  : NW.gray200,
                border: "none",
                borderRadius: 9,
                padding: "7px 16px",
                color: resultsActive ? "#FFFFFF" : NW.gray500,
                fontWeight: 700,
                fontSize: 12,
                cursor: resultsActive ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: resultsActive
                  ? `0 3px 10px rgba(24,75,140,0.22)`
                  : "none",
                fontFamily: THEME.fonts.body,
                transition: "all 0.2s",
                opacity: resultsActive ? 1 : 0.52,
                whiteSpace: "nowrap",
              }}
            >
              💾 Enregistrer
            </button>

            <SavedQueriesDropdown
              onSelect={handleLoadSavedQuery}
              refreshToken={saveRefreshToken}
              currentType="mdx"
            />

            {/* Séparateur */}
            <div
              style={{
                width: 1,
                height: 28,
                background: NW.gray200,
                flexShrink: 0,
              }}
            />

            {/* Onglets */}
            <div
              style={{
                display: "flex",
                background: NW.white,
                border: `1.5px solid ${NW.gray200}`,
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
              }}
            >
              {viewTabs.map((tab) => {
                const active = viewMode === tab.key;
                const activeBg =
                  tab.key === "table"
                    ? NW.violet
                    : NW.violet;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setViewMode(tab.key)}
                    style={{
                      padding: "7px 17px",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: THEME.fonts.body,
                      fontSize: 12,
                      background: active ? activeBg : "transparent",
                      color: active ? "#FFFFFF" : NW.gray500,
                      fontWeight: active ? 700 : 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      transition: "all 0.15s",
                      borderRight: `1px solid ${NW.gray200}`,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{tab.icon}</span>
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Barre chips Lignes / Colonnes */}
        {(rows.length > 0 || columns.length > 0) && (
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              padding: "8px 14px",
              background: NW.white,
              borderRadius: 10,
              border: `1.5px solid ${NW.gray200}`,
              boxShadow: "0 1px 6px rgba(24,75,140,0.07)",
            }}
          >
            {rows.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 5,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: NW.violet,
                    fontWeight: 700,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    fontFamily: THEME.fonts.heading,
                  }}
                >
                  ↕ Lignes
                </span>
                {rows.map((sel, i) => (
                  <span
                    key={i}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      dragPayload.current = { source: "rows", index: i, sel };
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 10px",
                      borderRadius: 8,
                      background: NW.violet,
                      color: "#FFFFFF",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      cursor: "grab",
                      boxShadow: "0 2px 8px rgba(24,75,140,0.2)",
                    }}
                  >
                    <span style={{ fontSize: 9, opacity: 0.5 }}>⠿</span>
                    <span>
                      {sel.hierarchyName ?? sel.dimensionName}
                      {sel.levelName && sel.levelName !== sel.hierarchyName && (
                        <span
                          style={{
                            fontSize: 10,
                            opacity: 0.7,
                            marginLeft: 4,
                          }}
                        >
                          · {sel.levelName}
                        </span>
                      )}
                    </span>
                    {/* FIX ② : clic × ne vide plus la table */}
                    <button
                      onClick={() => handleRemoveFromRows(i)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "rgba(255,255,255,0.8)",
                        fontSize: 15,
                        lineHeight: 1,
                        padding: "0 2px",
                      }}
                      title="Retirer (la table reste affichée)"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            {rows.length > 0 && columns.length > 0 && (
              <div
                style={{
                  width: 1,
                  height: 22,
                  background: NW.gray200,
                  flexShrink: 0,
                }}
              />
            )}
            {columns.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 5,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: NW.rose,
                    fontWeight: 700,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    fontFamily: THEME.fonts.heading,
                  }}
                >
                  ↔ Colonnes
                </span>
                {columns.map((sel, i) => (
                  <span
                    key={i}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      dragPayload.current = {
                        source: "columns",
                        index: i,
                        sel,
                      };
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 10px",
                      borderRadius: 8,
                      background: NW.rose,
                      color: "#FFFFFF",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      cursor: "grab",
                      boxShadow: "0 2px 8px rgba(125,168,217,0.2)",
                    }}
                  >
                    <span style={{ fontSize: 9, opacity: 0.5 }}>⠿</span>
                    <span>
                      {sel.hierarchyName ?? sel.dimensionName}
                      {sel.levelName && sel.levelName !== sel.hierarchyName && (
                        <span
                          style={{
                            fontSize: 10,
                            opacity: 0.7,
                            marginLeft: 4,
                          }}
                        >
                          · {sel.levelName}
                        </span>
                      )}
                    </span>
                    {/* FIX ② : clic × ne vide plus la table */}
                    <button
                      onClick={() => handleRemoveFromColumns(i)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "rgba(255,255,255,0.8)",
                        fontSize: 15,
                        lineHeight: 1,
                        padding: "0 2px",
                      }}
                      title="Retirer (la table reste affichée)"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Zone des résultats */}
        <div style={card({ padding: 18, minHeight: 240 })}>
  {showPrevisionView && viewMode === "table" ? (
    <div style={{ overflowX: "auto" }}>
      {previsionLoading && previsionFamilles.length === 0 && (
        <div style={{ padding: 24, color: NW.violet, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 10 }}>
          <span className="spinner" /> Chargement des prévisions IA…
        </div>
      )}
      {previsionFamilles.length > 0 && (
        <>
          {previsionLoading && (
            <div style={{ padding: "8px 0", color: NW.violet, fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span className="spinner" style={{ width: 12, height: 12 }} />
              Chargement en cours… {Object.keys(previsionData).length} / {previsionFamilles.length} familles
            </div>
          )}
          <table style={{ borderCollapse: "collapse", fontSize: 12, fontFamily: THEME.fonts.body, minWidth: 480, tableLayout: "auto" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              {/* Ligne 1 : header dim + groupes dépôts */}
              <tr>
                <th style={{
                  background: NW.violetDark, color: "#FFFFFF", padding: "10px 14px",
                  textAlign: "left", fontWeight: 700, fontSize: 12,
                  border: "1px solid rgba(255,255,255,0.12)", whiteSpace: "nowrap",
                  minWidth: 160, position: "sticky", left: 0, zIndex: 20,
                  borderRight: `3px solid ${NW.violet}`
                }}>
                  🗂 Famille
                </th>
                {depots.map(dep => (
                  <th key={dep} style={{
                    background: NW.rose, color: "#FFFFFF", padding: "8px 14px",
                    textAlign: "center", fontWeight: 700, fontSize: 12,
                    border: `1px solid ${NW.roseDark}`, whiteSpace: "nowrap", minWidth: 90
                  }}>
                    {dep}
                  </th>
                ))}
                <th style={{
                  background: NW.violetDark, color: NW.roseMed, padding: "8px 14px",
                  textAlign: "center", fontWeight: 800, fontSize: 12,
                  border: `1px solid ${NW.violet}`, whiteSpace: "nowrap", minWidth: 110,
                  borderLeft: `3px solid ${NW.rose}`
                }}>
                  ∑ TOTAL
                </th>
              </tr>
            </thead>
            <tbody>
              {previsionFamilles.map((famille, ri) => {
                const isEven = ri % 2 === 0;
                const rowBg = isEven ? NW.white : NW.gray50;
                const rowData = previsionData[famille] ?? {};
                const isLoading = previsionLoading && !previsionData[famille];
                const rowTotal = depots.reduce((s, dep) => s + (rowData[dep] ?? 0), 0);
                return (
                  <tr key={famille} style={{ background: rowBg }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = NW.violetSoft}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = rowBg}
                  >
                    <td style={{
                      padding: "8px 14px", fontWeight: 700, color: NW.violetDark,
                      background: isEven ? NW.violetSoft : "#EEF4FB",
                      border: `1px solid ${NW.violetMed}`, borderRight: `3px solid ${NW.violet}`,
                      position: "sticky", left: 0, zIndex: 2, whiteSpace: "nowrap"
                    }}>
                      {famille}
                    </td>
                    {isLoading ? (
                      <td colSpan={depots.length + 1} style={{ padding: "8px 14px", textAlign: "center", color: NW.gray500, fontSize: 11 }}>
                        <span className="spinner" style={{ width: 11, height: 11, display: "inline-block" }} /> calcul…
                      </td>
                    ) : (
                      <>
                        {depots.map(dep => {
                          const val = rowData[dep];
                          return (
                            <td key={dep} style={{
                              padding: "8px 14px", textAlign: "right",
                              border: `1px solid ${NW.gray200}`,
                              color: val ? NW.gray800 : NW.gray500,
                              background: rowBg, fontVariantNumeric: "tabular-nums"
                            }}>
                              {val ? val.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) : <span style={{ color: NW.gray300 }}>—</span>}
                            </td>
                          );
                        })}
                        <td style={{
                          padding: "8px 14px", textAlign: "right",
                          border: `1px solid ${NW.violet}`, borderLeft: `3px solid ${NW.rose}`,
                          color: rowTotal > 0 ? NW.violetDark : NW.gray500,
                          background: isEven ? NW.violetSoft : "#EEF4FB",
                          fontWeight: 800, fontVariantNumeric: "tabular-nums"
                        }}>
                          {rowTotal > 0 ? rowTotal.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) : <span style={{ color: NW.gray300 }}>—</span>}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {/* Footer totaux */}
            {!previsionLoading && previsionFamilles.length > 1 && (
              <tfoot style={{ position: "sticky", bottom: 0, zIndex: 5 }}>
                <tr>
                  <td style={{
                    padding: "9px 14px", background: NW.violetDark, color: "#FFFFFF",
                    fontWeight: 800, fontSize: 12, border: `1px solid ${NW.violet}`,
                    borderRight: `3px solid ${NW.rose}`, position: "sticky", left: 0, zIndex: 10, whiteSpace: "nowrap"
                  }}>
                    ∑ TOTAL GÉNÉRAL
                  </td>
                  {depots.map(dep => {
                    const total = previsionFamilles.reduce((s, f) => s + (previsionData[f]?.[dep] ?? 0), 0);
                    return (
                      <td key={dep} style={{
                        padding: "9px 14px", textAlign: "right",
                        background: NW.violetDark, color: NW.roseMed,
                        fontWeight: 700, fontSize: 12, border: `1px solid ${NW.violet}`,
                        fontVariantNumeric: "tabular-nums"
                      }}>
                        {total > 0 ? total.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) : "—"}
                      </td>
                    );
                  })}
                  <td style={{
                    padding: "9px 14px", textAlign: "right",
                    background: NW.violetDark, color: "#FCE7BF",
                    fontWeight: 900, fontSize: 13, border: `1px solid ${NW.violet}`,
                    borderLeft: `3px solid ${NW.rose}`, fontVariantNumeric: "tabular-nums"
                  }}>
                    {previsionFamilles.reduce((s, f) =>
                      s + depots.reduce((s2, dep) => s2 + (previsionData[f]?.[dep] ?? 0), 0), 0
                    ).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </>
      )}
    </div>
  ) : (
    <>
      {viewMode === "table" && (
        <PivotMatrix
          dragPayload={dragPayload}
          onDrop={handleDrop}
          active={resultsActive}
          animKey={animKey}
        />
      )}
      {viewMode === "dashboard" && <Dashboard />}
    </>
  )}
</div>

        {viewMode === "comparison" && (
          <ComparisonView
            initialComparisonPeriods={savedComparisonPeriods}
            autoRunToken={comparisonAutoRunToken}
          />
        )}
        {viewMode === "evolution" && <EvolutionView />}
      </div>

      {showSaveModal && (
        <SaveQueryModal
          onClose={() => setShowSaveModal(false)}
          onSaved={() => {
            setSaveRefreshToken((t) => t + 1);
          }}
          type="mdx"
          section={viewMode}
          overrideSoldeCodePeriode={null}
          overrideDemarquePhase={null}
        />
      )}
    </div>
  );
};

export default AIQueryBuilder;
